import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Zap, ArrowRight, Lock } from "lucide-react";

/**
 * UpgradeNudge. context-aware upgrade prompts.
 *
 * Three variants:
 * <UpgradeNudge variant="quota" />. quota usage widget for Dashboard
 * <UpgradeNudge variant="locked" />. generic "this feature is locked"
 * <UpgradeNudge variant="compass" />. Compass-tab banner for non-Shield users
 *
 * All variants are calm-mode (no red, no dark patterns) but visible and
 * actionable. Hidden automatically for users who are already on the relevant
 * paid tier so we don't nag paying customers.
 */

const PAID_TIERS = new Set(["vault", "copilot", "dispute", "pro"]);
const COMPASS_OR_BETTER = new Set(["copilot", "dispute", "pro"]);

export function UpgradeNudge({ variant = "locked", title, description, ctaLabel = "See plans", className = "" }) {
 const { user } = useAuth();
 if (!user) return null;

 // Auto-hide nudges that don't apply to the user's current tier.
 if (variant === "compass" && COMPASS_OR_BETTER.has(user.tier)) return null;
 if (variant === "locked" && PAID_TIERS.has(user.tier)) return null;

 if (variant === "quota") return <QuotaNudge user={user} className={className} />;

 const Icon = variant === "compass" ? Sparkles : Lock;
 const accent = variant === "compass" ? "#1B6C62" : "#B35D4C";
 const defaultTitle = variant === "compass" ? "Unlock the Compass AI" : "This needs a paid plan";
 const defaultDescription = variant === "compass"
 ? "Quick AI gives you correct, high-level overviews. Deep AI goes further. It reads your state's Act, reads your documents, drafts your letters and tribunal forms and gives you full step-by-step procedures. 100 Q&As/month, AUD$19.99."
 : "Upgrade to Vault, Shield, Dispute Mode or Pro to use this feature.";

 return (
 <div
 className={`card border-l-4 ${className}`}
 style={{ borderLeftColor: accent }}
 data-testid={`upgrade-nudge-${variant}`}
 >
 <div className="flex items-start gap-3">
 <div className="w-9 h-9 flex items-center justify-center bg-[#FAF9F6] border border-[#E5E4DE] flex-shrink-0">
 <Icon size={15} strokeWidth={1.75} style={{ color: accent }} />
 </div>
 <div className="flex-1 min-w-0">
 <h4 className="font-serif text-base text-[#0D2B52] mb-1">{title || defaultTitle}</h4>
 <p className="text-[13px] text-[#1A1A1A] font-serif leading-snug mb-3">{description || defaultDescription}</p>
 <Link
 to="/pricing"
 className="btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
 data-testid={`upgrade-cta-${variant}`}
 >
 {ctaLabel} <ArrowRight size={12} strokeWidth={1.75} />
 </Link>
 </div>
 </div>
 </div>
 );
}

function QuotaNudge({ user, className }) {
 const used = user.ai_queries_used ?? 0;
 const limit = user.ai_queries_limit ?? 5;
 const remaining = Math.max(0, limit - used);
 const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
 const isFree = user.tier === "free" || !user.tier;
 const isLow = remaining <= 1;
 const isOver = remaining === 0;

 // Hide for top-tier (Dispute / Pro), they have plenty of headroom.
 if (limit > 1000) return null;

 // Hide on paid tiers if usage is below 70%. no need to nag paying customers.
 if (!isFree && pct < 70) return null;

 const accent = isOver ? "#B35D4C" : isLow ? "#C9A646" : "#1B6C62";
 const headline = isOver
 ? "You're out of AI questions this month"
 : isLow
 ? `Only ${remaining} AI question${remaining === 1 ? "" : "s"} left`
 : `${used} of ${limit} AI questions used`;

 const desc = isFree
 ? "Quick AI gives you accurate overviews. Upgrade to Deep AI for full step-by-step procedures, your documents read for you and letters and tribunal forms drafted for you. 100 questions/month."
 : "You're approaching this month's limit. Upgrade for more headroom.";

 return (
 <div
 className={`card border-l-4 ${className}`}
 style={{ borderLeftColor: accent }}
 data-testid="upgrade-nudge-quota"
 >
 <div className="flex items-start gap-3">
 <div className="w-9 h-9 flex items-center justify-center bg-[#FAF9F6] border border-[#E5E4DE] flex-shrink-0">
 <Zap size={15} strokeWidth={1.75} style={{ color: accent }} />
 </div>
 <div className="flex-1 min-w-0">
 <h4 className="font-serif text-base text-[#0D2B52] mb-1">{headline}</h4>
 <div className="w-full bg-[#E5E4DE] h-1.5 mb-2.5 overflow-hidden">
 <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: accent }} />
 </div>
 <p className="text-[13px] text-[#1A1A1A] font-serif leading-snug mb-3">{desc}</p>
 <Link
 to="/pricing"
 className="btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
 data-testid="upgrade-cta-quota"
 >
 {isFree ? "Upgrade to Shield" : "See plans"} <ArrowRight size={12} strokeWidth={1.75} />
 </Link>
 </div>
 </div>
 </div>
 );
}

export default UpgradeNudge;
